import { Link } from '@solidjs/router';
import { gql } from 'graphql-request';
import { For, Show, onCleanup, onMount, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import headerStore from '../../stores/header-store';
import theme from '../../style/theme';
import { ErrorCatcher } from '../../tools/error-catcher';
import urlMatches from '../../tools/url-match';
import { Section } from '../../grid-system/section/section';
import { NewAtosLogoDeepBlue } from '../icons-library/new-atos-logo-deep-blue';
import { MenuBarsIcon } from '../icons-library/menu-bars';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';
import removeAmpersand from '../../tools/remove-ampersand';
import { GET_PRIMARY_MENU_QUERY } from '../menu/menu';
import { PrimaryMenuData } from '../menu/menu-types';
import { HeaderDropdownMenu } from './header-dropdown-menu';
import { headerNavItemClasses, publishHeaderHeight } from './shared';
import clsx from 'clsx';
import { HeaderNavigationShortcuts } from './header-navigation-shortcuts';
import { SearchPanel } from '../search/search-panel';

const GET_HEADER_QUERY = gql`
    query GetHeader($key: String!) {
        header: resource(key: $key) {
            content
            key
        }
    }
`;

type HeaderLink = {
    title: string;
    url: string;
    target: string;
};

export const HeaderContainer = () => {
    const { appStore, createCachedResource, viewport, localize } = useContext(AppContext);
    /* Audience-aware menu query. Mirrors the pattern in menu.tsx#getMenuLocation. */
    const menuKey = () => `/wp-json/content/v1/menus/${appStore.isHcp() ? 'primary_menu_hcp' : 'primary_menu'}`;
    const [headerContent] = createCachedResource(GET_HEADER_QUERY, () => ({ key: '/wp-json/rawb/v1/header' }), true);
    const [primaryMenuContent] = createCachedResource(GET_PRIMARY_MENU_QUERY, () => ({ key: menuKey() }), true);

    const primaryMenuData = (): PrimaryMenuData => primaryMenuContent()?.menu?.content;
    const isOriginalTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;
    const { open } = appStore;
    const { floatHeader: isHeaderCollapsed, setFloatHeader: setIsHeaderCollapsed } = headerStore;

    let headerRef: HTMLElement | undefined;

    onMount(() => {
        if (headerRef) {
            onCleanup(publishHeaderHeight(headerRef));
        }
    });

    /* Direction-aware header collapse:
       - at top of page (scrollY < TOP_THRESHOLD): always expanded
       - scrolling DOWN past threshold: collapse
       - scrolling UP past threshold: expand
       Driven by wheel + touchmove for immediate response, and also by scroll-direction delta
       so scrollbar drag / arrow keys / Page Up/Down also trigger collapse. A post-toggle
       cooldown ignores the scroll events that fire from the header's own collapse reflow,
       which would otherwise flip state mid-transition. */
    onMount(() => {
        // Hysteresis: expand only below EXPAND_THRESHOLD, collapse only above COLLAPSE_THRESHOLD; between the two,
        // no action (keep current state). This dead zone plus the cooldown below prevents flip-flopping at a boundary.
        const EXPAND_THRESHOLD = 40;
        // Near the top, collapsing removes the header's own height from the document and drops scrollY by roughly
        // that amount. If that drop crosses back under EXPAND_THRESHOLD the header force-expands, whose reflow pushes
        // scrollY back up -> collapse -> ... the near-top flicker. Keeping the collapse point above
        // (EXPAND_THRESHOLD + header height delta) means a collapse can never reflow us back under the expand point,
        // so the state settles. 160 sits comfortably above the header's collapse height delta.
        const COLLAPSE_THRESHOLD = 160;
        const MIN_DELTA = 2;
        // TOGGLE_COOLDOWN_MS must EXCEED the 300ms CSS transition. When it equalled the transition, the transition's
        // final reflow scroll event landed on the cooldown boundary and flipped the header, starting the next cycle
        // (the oscillation). 500ms leaves a safe margin so the reflow's trailing scroll events are fully absorbed.
        const TOGGLE_COOLDOWN_MS = 500;
        let touchPrevY = 0;
        let lastScrollY = window.scrollY;
        let cooldownUntil = 0;
        // Scrollbar drag produces rapid continuous scroll events that fight the CSS collapse
        // transition and cause jumpy behaviour. Mousedown fires when grabbing the scrollbar thumb;
        // wheel and keyboard paths don't set mouse-down, so we can use it to skip direction
        // detection for scrollbar interactions only.
        let mousePressed = false;
        const onMouseDown = () => { mousePressed = true; };
        const onMouseUp = () => { mousePressed = false; };

        // Single choke point for state changes: EVERY collapse/expand (including the at-top auto-expand) arms the
        // cooldown. Previously the at-top expand toggled without arming it, so the expand's own reflow (which bumps
        // scrollY back over the collapse threshold) triggered an immediate collapse, whose reflow dropped scrollY
        // under the top threshold, re-expanding... the flicker. Arming the cooldown on the expand blocks that.
        const applyCollapsed = (next: boolean) => {
            if (next === isHeaderCollapsed()) return;
            setIsHeaderCollapsed(next);
            cooldownUntil = performance.now() + TOGGLE_COOLDOWN_MS;
        };

        const setCollapsed = (direction: 'up' | 'down') => {
            const y = window.scrollY;
            if (y < EXPAND_THRESHOLD) {
                applyCollapsed(false);
                return;
            }
            // Honour the post-toggle cooldown on EVERY input path (wheel/touch used to bypass it and could
            // re-flip the header mid-transition).
            if (performance.now() < cooldownUntil) return;
            if (y < COLLAPSE_THRESHOLD) return;  // hysteresis dead zone
            applyCollapsed(direction === 'down');
        };

        const handleWheel = (e: WheelEvent) => {
            if (Math.abs(e.deltaY) < MIN_DELTA) return;
            setCollapsed(e.deltaY > 0 ? 'down' : 'up');
        };

        const handleTouchStart = (e: TouchEvent) => {
            touchPrevY = e.touches[0]?.clientY ?? 0;
        };
        const handleTouchMove = (e: TouchEvent) => {
            const y = e.touches[0]?.clientY ?? 0;
            const dy = touchPrevY - y; // finger moves up = scrolling content down
            if (Math.abs(dy) < MIN_DELTA) return;
            setCollapsed(dy > 0 ? 'down' : 'up');
            touchPrevY = y;
        };

        /* Scroll-direction delta — drives collapse for arrow keys, Page Up/Down, Space/Shift+Space,
           anchor-link jumps. Skipped during scrollbar drag (mousePressed) since rapid scroll events
           fight the header's CSS collapse transition. At-top auto-expand still runs regardless. */
        const handleScroll = () => {
            const y = window.scrollY;

            if (y < EXPAND_THRESHOLD) {
                applyCollapsed(false);
                lastScrollY = y;
                return;
            }

            // Scrollbar drag — skip direction detection; keep lastScrollY fresh so direction-based
            // logic re-engages cleanly once the mouse is released.
            if (mousePressed) {
                lastScrollY = y;
                return;
            }

            // Cooldown: skip scroll events fired by the header's own reflow after a toggle.
            if (performance.now() < cooldownUntil) {
                lastScrollY = y;
                return;
            }

            // Accumulate delta across events — slow keyboard scrolls (Page Down on some setups
            // can produce small increments); updating lastScrollY only when we act lets those
            // deltas add up instead of being thrown away per event.
            const dy = y - lastScrollY;
            if (Math.abs(dy) < MIN_DELTA) return;
            lastScrollY = y;
            setCollapsed(dy > 0 ? 'down' : 'up');
        };

        window.addEventListener('wheel', handleWheel, { passive: true });
        window.addEventListener('touchstart', handleTouchStart, { passive: true });
        window.addEventListener('touchmove', handleTouchMove, { passive: true });
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('mousedown', onMouseDown, { passive: true });
        window.addEventListener('mouseup', onMouseUp, { passive: true });
        handleScroll();

        onCleanup(() => {
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mouseup', onMouseUp);
        });
    });

    /* Inner header body. Function form (not JSX constant) so the reactive accessors inside
       re-evaluate on state change. */
    const HeaderInner = () => (
        <div class="relative w-full z-[2]">
            {/* Top row — single logo (smoothly resizes between big and small) + audience toggle + icon buttons.
            On scroll the logo container shrinks to the compact size; the audience/icons cluster fades out and
            collapses its width, so only the small logo remains visible above the nav. Row 2 below stays pinned. */}
            <div
                id="header-top-row"
                class={clsx(
                    'flex items-center relative z-[2] gap-cvi-2 transition-[padding] duration-300 ease-in-out',
                    isHeaderCollapsed() ? 'pb-cvi-3 pt-cvi-5' : 'py-cvi-6'
                )}
            >
                {/* Single logo — transitions width + height between rest and scrolled states.
                SVG preserves aspect ratio inside the container, so it appears to shrink gracefully. */}
                <Link
                    href="/"
                    class={clsx(
                        'block shrink-0 transition-[width,height] duration-300 ease-in-out',
                        isHeaderCollapsed() ? 'w-cvi-logo-sm-w h-cvi-logo-sm-h' : 'w-cvi-logo-lg-w h-cvi-logo-lg-h'
                    )}
                >
                    <NewAtosLogoDeepBlue width="100%" height="100%" />
                </Link>

                {/* Audience toggle + icon cluster — fades out and collapses both dimensions to 0 on scroll
                (width AND height, so the row height is driven by the logo alone when scrolled).
                Explicit max-w/max-h transitions animate smoothly in the parent's flex layout;
                open values are generous caps that never clip real content.
                The py/pr padding gives the clip box room for the 8px outer focus ring, and the
                matching negative margins keep it out of the layout. */}
                <div
                    class={clsx(
                        'flex items-start ml-auto pl-cvi-10 py-cvi-4 -my-cvi-4 pr-cvi-4 -mr-cvi-4 gap-cvi-9 max-desktop:gap-cvi-8 overflow-hidden',
                        'transition-[opacity,max-width,max-height] duration-300 ease-in-out',
                        isHeaderCollapsed() ? 'opacity-0 max-w-0 max-h-0 pointer-events-none' : 'opacity-100 max-w-full max-h-cvi-header-cluster-max'
                    )}
                >
                    <div class="flex items-center gap-cvi-9 max-desktop:gap-cvi-8">
                        <For each={headerContent().header.content.headerLinks}>
                            {(link: HeaderLink, index) => {
                                if (!link || !link.title || !link.url) {
                                    return null;
                                }

                                /* Active tab reflects the current audience: index 0 = PAC link, index 1 = HCP link. */
                                const isActiveAudienceTab = () => appStore.isHcp() ? index() === 1 : index() === 0;

                                return (
                                    <Link
                                        href={removeTrailingSlash(link.url)}
                                        target={link.target}
                                        class={clsx(
                                            'label-md py-cvi-4 self-center no-underline transition-colors duration-200 ease-in-out',
                                            'hover:text-cvi-fg-default',
                                            isActiveAudienceTab()
                                                ? 'font-cvi-bold text-cvi-fg-default border-b-2 border-cvi-border-interactive'
                                                : 'font-cvi-medium text-cvi-fg-subtle'
                                        )}
                                    >
                                        {link?.title ? removeAmpersand(link.title) : null}
                                    </Link>
                                );
                            }}
                        </For>
                    </div>
                    <HeaderNavigationShortcuts />
                </div>
            </div>

            {/* Bottom row — secondary navigation. Always visible on desktop; position-stable across scroll. */}
            <div
                id="header-bottom-row"
                class={clsx('flex justify-between items-start gap-cvi-11 w-full', isHeaderCollapsed() ? 'pb-0' : 'pb-cvi-5')}
            >
                <HeaderDropdownMenu primaryMenuData={primaryMenuData()} />
                {/* Utility row (Contact / Blog / Events). Audience-aware: HCP reads `headerHcp*` fields,
                PAC reads the non-prefixed `header*` equivalents. Individual links stay hidden if unpopulated. */}
                <nav class="flex">
                    <For
                        each={appStore.isHcp()
                            ? [
                                headerContent()?.header?.content?.headerHcpContactLink,
                                headerContent()?.header?.content?.headerHcpBlogsLink,
                                headerContent()?.header?.content?.headerHcpEventsLink,
                            ] : [
                                headerContent()?.header?.content?.headerContactLink,
                                headerContent()?.header?.content?.headerBlogsLink,
                                headerContent()?.header?.content?.headerEventsLink,
                            ]
                        }
                    >
                        {(link) => (
                            /* Defensive filter: ACF `link` fields can return truthy-but-empty shapes
                            ({url:'', title:'Events', ...}) or placeholder URLs like '#'. Require a real,
                            non-placeholder URL + a title before rendering. */
                            <Show when={link && link.url?.trim() && link.url.trim() !== '#' && link.title}>
                                <Link
                                    href={removeTrailingSlash(link.url)}
                                    target={link.target}
                                    class={clsx(
                                        headerNavItemClasses,
                                        'whitespace-nowrap no-underline px-cvi-6 py-cvi-5',
                                        urlMatches(link.url) ? 'font-cvi-bold' : 'font-cvi-medium'
                                    )}
                                >
                                    {removeAmpersand(link.title)}
                                </Link>
                            </Show>
                        )}
                    </For>
                </nav>
            </div>
        </div>
    );

    return (
        <ErrorCatcher componentName="Header">
            {/* Mobile/tablet: slides up + fades out when scrolling down (driven by the existing
                isHeaderCollapsed signal from headerStore, which the scroll listener above sets
                based on direction-aware scroll detection). On desktop, HeaderInner handles its
                own partial collapse animation; the transform here is a no-op there because
                isOriginalTablet() is false. */}
            <header
                ref={headerRef}
                data-hides-on-scroll={isOriginalTablet() ? '' : undefined}
                class={clsx(
                    '[container-type:inline-size] sticky z-[3] top-0 left-0 right-0 print:hidden bg-cvi-bg-body',
                    'transition-[transform,opacity] duration-300 ease-in-out',
                    isOriginalTablet() && isHeaderCollapsed() && '-translate-y-full opacity-0'
                )}
            >
                <Show when={headerContent()?.header?.content?.headerLinks}>
                    <Section
                        templateShorthand={[12]}
                        widthType="bgFull"
                        removeSpacingBlock={true}
                        customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
                    >
                        <Show
                            when={isOriginalTablet()}
                            fallback={<HeaderInner />}
                        >
                            <div class="flex items-center justify-between gap-cvi-2 py-cvi-5">
                                <Link href="/" class="block shrink-0 w-cvi-logo-sm-w h-cvi-logo-sm-h">
                                    <NewAtosLogoDeepBlue width="100%" height="100%" />
                                </Link>
                                <div class="flex items-center gap-cvi-2">
                                    <HeaderNavigationShortcuts />
                                    <button
                                        class="icon-button-ghost icon-button-ghost-md"
                                        onClick={open}
                                        aria-label={localize('menu-toggle', 'Menu Toggle')}
                                    >
                                        <MenuBarsIcon fill="currentColor" size="medium" ariaLabel={localize('menu-toggle', 'Menu toggle')} />
                                    </button>
                                </div>
                            </div>
                        </Show>
                    </Section>
                </Show>

                {/* Hangs off the header's bottom edge; see search-panel.tsx. */}
                <SearchPanel />
            </header>
        </ErrorCatcher>
    );
};
